import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { Address } from "@blaze-cardano/core";

import { GummiwormContext } from "./context";
import type { IGummiwormState } from "./types";
import { useDepositTxs } from "./hooks/useDepositTxs";
import { useWithdrawTxs } from "./hooks/useWithdrawTxs";
import { useAppContext } from "../AppContext";
import { Core } from "@blaze-cardano/sdk";
import { hashHexToU256 } from "@/lib/marketUtils";
import { useWebSocket } from "@/hooks/useWebSocket";
import { ensureGuestWsIdentity } from "@/lib/guestIdentity";

export const MOCK_USER_ACCOUNT_ID = 1n;

export const GummiwormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    state: { blaze },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const { depositTxs, addDepositTx, updateDepositTx, clearDepositTxs } = useDepositTxs();
  const { withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs } = useWithdrawTxs();
  const paymentPartHash = useMemo(() => {
    if (!mainAddress) return null;
    return Address.fromBech32(mainAddress).getProps().paymentPart?.hash ?? null;
  }, [mainAddress]);
  // Market data (order book, candles, tradingEnabled) is only ever pushed over this
  // per-connection channel, so it must open even without a wallet. A guest identity
  // never matches a real deposit account, so myAccountId/ledgerView.account (below)
  // stay null and all wallet-owner gating elsewhere is unaffected.
  const wsAccountHash = useMemo(
    () => paymentPartHash ?? ensureGuestWsIdentity(),
    [paymentPartHash],
  );
  const wsUrl = `${window.__APP_CONFIG.apiWsUrl}/ws`;
  // Reads use the plaintext channel protocol (no encryption); writes still use
  // `orderEncryptionKey` on the HTTP submit path, so it stays in context above.
  const { ledgerView, connected: wsConnected, error: wsError, send: sendWsMessage, watchMarket } = useWebSocket(wsUrl, wsAccountHash);
  const markets = useMemo(() => Object.values(ledgerView.markets) as any[], [ledgerView.markets]);
  const accountsReady = wsConnected;

  const tradingEnabled = useMemo(() => {
    if (!ledgerView) return false;
    return ledgerView.tradingEnabled;
  }, [ledgerView]);

  const myAccountId = useMemo(() => {
    if (!paymentPartHash) return null;
    return hashHexToU256(paymentPartHash);
  }, [paymentPartHash]);

  useEffect(() => {
    for (let deposit of Object.values(ledgerView?.deposits)) {
      if (deposit.absorbedInBlock !== 0n) {
        if (depositTxs.has(deposit.requestId.toString())) {
          updateDepositTx(deposit.requestId, { status: "absorbed" });
        }
        continue;
      }
      if (deposit.rejectedInBlock !== 0n) {
        updateDepositTx(deposit.requestId, {
          status: "rejected",
          errorMessage: deposit.failureReason ?? "Deposit was rejected by the network.",
        });
        continue;
      }
      if (deposit.refundTx) {
        const refundCbor = Buffer.from(deposit.refundTx).toString("hex");
        if (depositTxs.has(deposit.requestId.toString())) {
          updateDepositTx(deposit.requestId, { refundCbor, status: "confirmed" });
        }
        continue;
      }
      if (!depositTxs.has(deposit.requestId.toString())) {
        addDepositTx(deposit.requestId, {
          cbor: "",
          hash: "",
          refundCbor: "",
          status: "pending",
        });
      } else {
        updateDepositTx(deposit.requestId, { status: "pending" });
      }
    }
  }, [ledgerView?.deposits]);

  // ── Refund tx poller ────────────────────────────────────────────────
  // Hydrozoa mints a post-dated refund tx for every registered deposit as
  // a safety valve, and no longer pushes it to us via the ledger event log
  // (sugar-rush stopped tailing hydrozoa's rocksdb directly). Poll hydrozoa's
  // HTTP API for each deposit that doesn't have one yet.
  const pollingRefunds = useRef(new Set<string>());
  const depositTxsRef = useRef(depositTxs);
  useEffect(() => {
    depositTxsRef.current = depositTxs;
  }, [depositTxs]);

  useEffect(() => {
    const pollOnce = async () => {
      const pending = [...depositTxsRef.current.entries()].filter(
        ([, tx]) =>
          !tx.refundCbor &&
          !["absorbed", "refunded", "refunding", "failed"].includes(tx.status),
      );
      if (pending.length === 0) return;

      await Promise.all(
        pending.map(async ([id]) => {
          if (pollingRefunds.current.has(id)) return;
          pollingRefunds.current.add(id);
          try {
            const response = await fetch(
              `${window.__APP_CONFIG.apiBaseUrl}/head/requests/${id}`,
            );
            if (!response.ok) return;
            const data = await response.json();
            let refund = data.status?.relatedEffects?.find(e => e.kind == "refund");
            if (refund) {
              let refundTxId = refund.l1TxId;
              const getRefundBody = await fetch(
                `${window.__APP_CONFIG.apiBaseUrl}/head/effects/${refundTxId}`,
              );
              if (!getRefundBody.ok) return;
              const data = await getRefundBody.json();
              let refundBody = data.txCbor;
              updateDepositTx(BigInt(id), { refundCbor: refundBody });
            }
          } catch (err) {
            console.error("Error polling hydrozoa deposit status:", err);
          } finally {
            pollingRefunds.current.delete(id);
          }
        }),
      );
    };

    pollOnce();
    const interval = window.setInterval(pollOnce, 10_000);
    return () => window.clearInterval(interval);
  }, [updateDepositTx]);

  // ── Deposit reconciler ──────────────────────────────────────────────
  // Single place that handles L1 submission and confirmation monitoring.
  // Runs whenever depositTxs or blaze changes. Uses a ref to track which
  // deposits are already being handled so we don't double-submit.
  const handling = useRef(new Set<string>());

  useEffect(() => {
    if (!blaze || !blaze.provider) return;

    for (const [id, tx] of depositTxs.entries()) {
      if (handling.current.has(id)) continue;

      // Ready to submit: has both signed cbor and refund tx, but not yet submitted
      if (
        tx.cbor &&
        tx.refundCbor &&
        !tx.hash &&
        !["settling", "on-chain", "absorbed", "failed", "rejected", "refunded", "refunding"].includes(tx.status)
      ) {
        handling.current.add(id);
        (async () => {
          try {
            const signedTx = Core.Transaction.fromCbor(Core.TxCBOR(tx.cbor));
            const hash = await blaze.submitTransaction(signedTx);
            updateDepositTx(BigInt(id), { status: "settling", hash });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes("already submitted") || msg.includes("AlreadyExists")) {
              // Someone else submitted — that's fine
            } else {
              updateDepositTx(BigInt(id), {
                status: "failed",
                errorMessage: `L1 transaction submission failed: ${msg}`,
              });
            }
          } finally {
            handling.current.delete(id);
          }
        })();
      }

      // Settling: monitor for L1 confirmation
      if (tx.status === "settling" && tx.hash) {
        handling.current.add(id);
        blaze.provider
          .awaitTransactionConfirmation(tx.hash, 120_000)
          .then((confirmed) => {
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "on-chain" });
            } else {
              updateDepositTx(BigInt(id), {
                status: "failed",
                errorMessage:
                  "Deposit transaction was not confirmed on L1 within the expected window.",
              });
            }
          })
          .catch((err) => {
            console.error("Error monitoring L1 tx confirmation:", err);
          })
          .finally(() => {
            handling.current.delete(id);
          });
      }

      // Rejected with refund available: submit refund tx
      if (tx.status === "rejected" && tx.refundCbor && !tx.hash) {
        handling.current.add(id);
        (async () => {
          try {
            updateDepositTx(BigInt(id), { status: "refunding" });
            const refundTx = Core.Transaction.fromCbor(Core.TxCBOR(tx.refundCbor));
            const refundHash = await blaze.submitTransaction(refundTx);
            updateDepositTx(BigInt(id), { hash: refundHash, status: "refunding" });
            const confirmed = await blaze.provider.awaitTransactionConfirmation(
              refundHash,
              120_000,
            );
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "refunded" });
            }
          } catch (err) {
            console.error("Error submitting refund tx:", err);
          } finally {
            handling.current.delete(id);
          }
        })();
      }

      // Refunding: monitor for confirmation
      if (tx.status === "refunding" && tx.hash) {
        handling.current.add(id);
        blaze.provider
          .awaitTransactionConfirmation(tx.hash, 120_000)
          .then((confirmed) => {
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "refunded" });
            }
          })
          .catch((err) => {
            console.error("Error monitoring refund tx:", err);
          })
          .finally(() => {
            handling.current.delete(id);
          });
      }
    }
  }, [depositTxs, blaze, updateDepositTx]);

  const contextValue = useMemo<IGummiwormState>(
    () => ({
      accountsReady,
      markets,
      myAccountId,
      error: wsError,
      tradingEnabled,
      depositTxs,
      clearDepositTxs,
      withdrawTxs,
      addWithdrawTx,
      updateWithdrawTx,
      clearWithdrawTxs,
      sendWsMessage,
      watchMarket,
      ledgerView,
    }),
    [wsConnected, wsError, markets, myAccountId, tradingEnabled, depositTxs, clearDepositTxs, withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs, sendWsMessage, watchMarket, ledgerView],
  );

  return (
    <GummiwormContext.Provider value={contextValue}>
      {children}
    </GummiwormContext.Provider>
  );
};
